import { useState, useRef } from 'react';
import { Upload, X } from '@phosphor-icons/react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SingleImageUploader({ image, onImageChange, label = "Image", testId = "single-image" }) {
    const [uploading, setUploading] = useState(false);
    const fileInputRef = useRef(null);

    const handleFileSelect = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (!file.type.startsWith('image/')) {
            toast.error(`${file.name} n'est pas une image`);
            return;
        }

        if (file.size > 5 * 1024 * 1024) {
            toast.error(`${file.name} dépasse 5MB`);
            return;
        }

        setUploading(true);
        try {
            const formData = new FormData();
            formData.append('file', file);
            
            const response = await axios.post(`${API}/upload`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            
            const imageUrl = `${process.env.REACT_APP_BACKEND_URL}${response.data.url}`;
            onImageChange(imageUrl);
            toast.success(`${label} uploadée`);
        } catch (error) {
            console.error('Upload error:', error);
            toast.error(`Erreur upload ${label}`);
        } finally {
            setUploading(false);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    const removeImage = () => {
        onImageChange('');
    };

    return (
        <div className="space-y-3">
            <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                className="hidden"
                data-testid={`${testId}-file-input`}
            />
            
            {image ? (
                <div className="relative group inline-block">
                    <div className="w-32 h-32 rounded-lg overflow-hidden border border-border">
                        <img
                            src={image}
                            alt={label}
                            className="w-full h-full object-cover"
                        />
                    </div>
                    <div className="flex gap-2 mt-2">
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => fileInputRef.current?.click()}
                            disabled={uploading}
                            data-testid={`${testId}-change-btn`}
                        >
                            <Upload size={14} className="mr-1" />
                            {uploading ? 'Upload...' : 'Changer'}
                        </Button>
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={removeImage}
                            className="text-red-500"
                            data-testid={`${testId}-remove-btn`}
                        >
                            <X size={14} className="mr-1" />
                            Retirer
                        </Button>
                    </div>
                </div>
            ) : (
                <Button
                    type="button"
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className="h-32 w-32 border-dashed flex flex-col items-center justify-center gap-2"
                    data-testid={`${testId}-upload-btn`}
                >
                    {uploading ? (
                        <>
                            <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-current" />
                            <span className="text-xs">Upload...</span>
                        </>
                    ) : (
                        <>
                            <Upload size={20} />
                            <span className="text-xs text-center">Uploader<br/>{label}</span>
                        </>
                    )}
                </Button>
            )}
        </div>
    );
}
